import { Injectable } from '@angular/core';

/**
 * Service utilitaire pour l'évaluation des conditionsCreationMap.
 *
 * Les conditionsCreationMap définissent des conditions XPath-like pour identifier quel élément
 * d'un tableau correspond à quel champ. Ce service fournit des méthodes mutualisées pour
 * évaluer ces conditions, utilisées par :
 * - FormInputFactory (lors de la création standard)
 * - InputIdEnrichmentService (lors de l'import)
 *
 * Formats de conditions supportés :
 * - "[ID/&text()='value']" : vérifie si ID.val === 'value'
 * - "[not(ID/&text()=('value1','value2'))]" : vérifie si ID.val n'est pas dans la liste
 * - "[path/&listName='value']" : vérifie si path.listName === 'value'
 * - "[not(FieldName)]" : vérifie si le champ FieldName n'existe pas ou est vide
 */
@Injectable({
  providedIn: 'root'
})
export class ConditionsCreationMapService {

  /**
   * Vérifie si un élément correspond à toutes les conditions du conditionsCreationMap.
   *
   * @param element L'élément du tableau à vérifier
   * @param conditions Les conditions à évaluer (ex: ["[ID/&text()='CrossBorderLaw']"])
   * @returns true si toutes les conditions sont satisfaites
   */
  matchesConditions(element: any, conditions: string[]): boolean {
    if (!element || !conditions || conditions.length === 0) {
      return true;
    }

    for (const condition of conditions) {
      if (!this.evaluateCondition(condition, element)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Vérifie les conditions pour plusieurs contextes de tableaux.
   *
   * @param conditionsCreationMap Map des conditions par nom de tableau
   * @param arrayContexts Map des contextes de tableaux (nom du tableau -> élément)
   * @returns true si toutes les conditions sont satisfaites pour tous les tableaux
   */
  matchesConditionsWithContexts(
    conditionsCreationMap: { [key: string]: string[] },
    arrayContexts: Map<string, any>
  ): boolean {
    if (!conditionsCreationMap) {
      return true;
    }

    for (const [arrayName, conditions] of Object.entries(conditionsCreationMap)) {
      if (!conditions || conditions.length === 0) {
        continue;
      }

      // ✅ FIX 2026-03-10: Vérifier si les conditions utilisent '../'
      // Si oui, il faut chercher dans le parent du tableau
      const hasParentReference = conditions.some(cond => cond.includes('../'));

      let element: any;

      if (hasParentReference) {
        // Trouver le parent : chercher le tableau qui contient arrayName
        // Par exemple, si arrayName est 'attachment', chercher 'callfortendersdocumentreference'
        element = this.findParentInContexts(arrayName, arrayContexts);
      } else {
        element = arrayContexts.get(arrayName.toLowerCase());
      }

      if (!element) {
        // Si le tableau n'est pas dans le contexte, ignorer
        continue;
      }

      if (!this.matchesConditions(element, conditions)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Trouve l'élément parent d'un tableau dans arrayContexts.
   * Par exemple, pour 'attachment', renvoie l'élément de 'callfortendersdocumentreference'
   */
  private findParentInContexts(childArrayName: string, arrayContexts: Map<string, any>): any {
    const childLower = childArrayName.toLowerCase();

    // Parcourir tous les contextes pour trouver celui qui contient l'enfant
    for (const [contextName, contextElement] of arrayContexts.entries()) {
      if (contextName === childLower) {
        // C'est l'enfant lui-même, pas le parent
        continue;
      }

      // Vérifier si contextElement contient une propriété qui correspond à childArrayName
      if (contextElement && typeof contextElement === 'object') {
        const actualKey = this.findKeyIgnoreCase(contextElement, childLower);
        if (actualKey) {
          // Trouvé ! contextElement est le parent
          return contextElement;
        }
      }
    }

    return null;
  }

  /**
   * Évalue une condition XPath-like sur un élément.
   */
  evaluateCondition(condition: string, element: any): boolean {
    if (!condition || !element) {
      return true;
    }

    // Nettoyer la condition (enlever les crochets externes)
    const cleanCondition = condition.replace(/^\[|\]$/g, '');

    // Vérifier si c'est une condition négative "not(...)"
    const isNegative = cleanCondition.startsWith('not(');
    const innerCondition = isNegative
      ? cleanCondition.replace(/^not\(|\)$/g, '')
      : cleanCondition;

    // Cas spécial: not(FieldName) sans autre opérateur - vérifie l'absence du champ
    if (isNegative && /^\w+$/.test(innerCondition)) {
      return this.evaluateFieldAbsenceCondition(innerCondition, element);
    }

    const result = this.parseAndEvaluateSimpleCondition(innerCondition, element);
    return isNegative ? !result : result;
  }

  /**
   * Évalue une condition d'absence de champ: not(FieldName)
   */
  private evaluateFieldAbsenceCondition(fieldName: string, element: any): boolean {
    const fieldToCheck = fieldName.toLowerCase();
    const actualKey = this.findKeyIgnoreCase(element, fieldToCheck);

    if (!actualKey) {
      return true; // Le champ n'existe pas
    }

    const fieldValue = element[actualKey];

    // Vérifier si le champ est vide
    if (fieldValue === undefined || fieldValue === null) {
      return true;
    }

    // Pour un tableau, vérifier s'il est vide
    if (Array.isArray(fieldValue)) {
      return fieldValue.length === 0;
    }

    return false; // Le champ existe et n'est pas vide
  }

  /**
   * Parse et évalue une condition simple (sans 'not').
   */
  private parseAndEvaluateSimpleCondition(condition: string, element: any): boolean {
    // Format: "path/&text()='value'" ou "path/&text()=('value1','value2')"
    const textMatch = condition.match(/^(.+?)\/&text\(\)\s*=\s*(.+)$/);
    if (textMatch) {
      return this.evaluateTextCondition(textMatch[1], textMatch[2], element);
    }

    // Format: "path/&listName='value'"
    const listNameMatch = condition.match(/^(.+?)\/&listName\s*=\s*'([^']+)'$/);
    if (listNameMatch) {
      return this.evaluateListNameCondition(listNameMatch[1], listNameMatch[2], element);
    }

    // Format: "path/&schemeName='value'" (ex: [ID/&schemeName='Lot'] pour distinguer Lot vs LotsGroup)
    const schemeNameMatch = condition.match(/^(.+?)\/&schemeName\s*=\s*'([^']+)'$/);
    if (schemeNameMatch) {
      return this.evaluateSchemeNameCondition(schemeNameMatch[1], schemeNameMatch[2], element);
    }

    // Si on ne peut pas parser la condition, retourner true par défaut
    return true;
  }

  /**
   * Évalue une condition sur le schemeName (path/&schemeName='value').
   * Ex: [ID/&schemeName='Lot'] → vérifie que element.id.schemeName === 'Lot'
   * Dans le JSON, id peut être un objet {val, schemeName} ou un tableau d'objets.
   */
  private evaluateSchemeNameCondition(path: string, expectedSchemeName: string, element: any): boolean {
    const targetObj = this.getObjectAtPath(element, path);

    if (!targetObj) {
      return false;
    }

    // targetObj peut être un objet unique ou un tableau
    const items = Array.isArray(targetObj) ? targetObj : [targetObj];

    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      const schemeNameKey = this.findKeyIgnoreCase(item, 'schemename');
      if (schemeNameKey && item[schemeNameKey] === expectedSchemeName) {
        return true;
      }
    }
    return false;
  }

  /**
   * Évalue une condition sur la valeur texte (path/&text()='value')
   */
  private evaluateTextCondition(path: string, valueExpr: string, element: any): boolean {
    // ✅ FIX 2026-03-10: Gérer le chemin relatif parent '../'
    // Si le chemin commence par '../', on l'enlève car matchesConditionsWithContexts
    // a déjà résolu le parent
    let actualPath = path;
    if (path.startsWith('../')) {
      actualPath = path.substring(3);
    }

    const actualValue = this.getValueAtPath(element, actualPath);
    const trimmedValueExpr = valueExpr.trim();


    // Si actualValue est undefined ou null, la condition ne peut pas être satisfaite
    if (actualValue === undefined || actualValue === null) {
      return false;
    }

    // Vérifier si c'est une liste de valeurs ('val1','val2')
    if (trimmedValueExpr.startsWith('(') && trimmedValueExpr.endsWith(')')) {
      const values = trimmedValueExpr
        .slice(1, -1)
        .split(',')
        .map(v => v.trim().replace(/^'|'$/g, ''));
      return values.includes(actualValue);
    } else {
      // Valeur unique
      const expectedValue = trimmedValueExpr.replace(/^'|'$/g, '');
      return actualValue === expectedValue;
    }
  }

  /**
   * Évalue une condition sur le listName (path/&listName='value')
   */
  private evaluateListNameCondition(path: string, expectedListName: string, element: any): boolean {
    const targetObj = this.getObjectAtPath(element, path);


    if (!targetObj) {
      return false;
    }

    // Chercher la propriété listName en ignorant la casse
    const listNameKey = this.findKeyIgnoreCase(targetObj, 'listname');
    if (!listNameKey) {
      return false;
    }

    return targetObj[listNameKey] === expectedListName;
  }

  /**
   * Récupère la valeur à un chemin donné dans un objet.
   * Le chemin peut contenir des / pour la navigation.
   */
  private getValueAtPath(obj: any, path: string): any {
    if (!obj || !path) {
      return undefined;
    }

    const parts = path.split('/');
    let current = obj;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!current) {
        return undefined;
      }

      const actualKey = this.findKeyIgnoreCase(current, part);
      if (!actualKey) {
        return undefined;
      }

      current = current[actualKey];

      // Convertir null en undefined immédiatement
      if (current === null) {
        return undefined;
      }

      // Si c'est un tableau, prendre le premier élément pour continuer la navigation
      if (Array.isArray(current) && current.length > 0) {
        current = current[0];
        // Vérifier si le premier élément est null
        if (current === null) {
          return undefined;
        }
      }
    }

    // Retourner la valeur 'val' ou 'value' si c'est un objet avec ces propriétés, sinon l'objet lui-même
    let finalValue;
    if (current && typeof current === 'object' && !Array.isArray(current)) {
      // Chercher la propriété 'val' ou 'value' en ignorant la casse
      const valKey = this.findKeyIgnoreCase(current, 'val');
      const valueKey = this.findKeyIgnoreCase(current, 'value');

      // Priorité à 'val', sinon 'value', sinon l'objet lui-même
      if (valKey && current[valKey] !== null && current[valKey] !== undefined && current[valKey] !== '') {
        finalValue = current[valKey];
      } else if (valueKey && current[valueKey] !== null && current[valueKey] !== undefined && current[valueKey] !== '') {
        // Utiliser 'value' si 'val' n'est pas valide
        finalValue = current[valueKey];
      } else if (valKey || valueKey) {
        // Si 'val' ou 'value' existe mais sont null/undefined/vide, retourner undefined
        return undefined;
      } else {
        // Si pas de propriété 'val' ou 'value', retourner l'objet lui-même
        finalValue = current;
      }
    } else {
      finalValue = current;
    }


    // Dernière vérification : retourner undefined si la valeur est null
    return finalValue === null ? undefined : finalValue;
  }

  /**
   * Récupère l'objet à un chemin donné (sans extraire .val).
   */
  private getObjectAtPath(obj: any, path: string): any {
    if (!obj || !path) {
      return undefined;
    }

    const parts = path.split('/');
    let current = obj;

    for (const part of parts) {
      if (!current) {
        return undefined;
      }

      const actualKey = this.findKeyIgnoreCase(current, part);
      if (!actualKey) {
        return undefined;
      }

      current = current[actualKey];

      // Si c'est un tableau, prendre le premier élément pour continuer la navigation
      if (Array.isArray(current) && current.length > 0) {
        current = current[0];
      }
    }

    return current;
  }

  /**
   * Trouve une clé dans un objet en ignorant la casse.
   */
  private findKeyIgnoreCase(obj: any, keyName: string): string | null {
    if (!obj || typeof obj !== 'object') {
      return null;
    }

    const lowerKey = keyName.toLowerCase();
    for (const key of Object.keys(obj)) {
      if (key.toLowerCase() === lowerKey) {
        return key;
      }
    }
    return null;
  }
}

